import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    Button,
    Container,
    useMediaQuery,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Logo from '../assets/logo.png';
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { useDarkMode } from '../contexts/DarkModeContext';

const navItems = ['header.about', 'header.services', 'header.contact'];

const useParallax = () => {
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            setOffset(window.pageYOffset);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return offset;
};

export default function Header() {
    const {t} = useTranslation();
    const theme = useTheme();
    const { darkMode, toggleDarkMode } = useDarkMode();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const scrollOffset = useParallax();

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMouseEnter = (item) => {
        setActiveItem(item);
    };

    const handleMouseLeave = () => {
        setActiveItem(null);
    };

    return (
        <AppBar
            position="sticky"
            color="transparent"
            elevation={0}
            sx={{
                backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(20px)',
                borderBottom: scrollOffset > 10 ? `1px solid ${darkMode ? '#3F3F46' : '#E5E5E7'}` : 'none',
                transition: 'all 0.3s ease'
            }}
        >
            <Container maxWidth="100%">
                <Toolbar
                    disableGutters
                    sx={{
                        justifyContent: 'space-between',
                        py: { xs: 1, md: 1.5 },
                        px: { xs: 2, md: 4 },
                        maxWidth: '1024px',
                        mx: 'auto',
                        width: '100%'
                    }}
                >
                    {/* Logo + site title */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexGrow: 1
                        }}
                    >
                        <Box
                            component="img"
                            src={Logo}
                            alt="aidex.co logo"
                            sx={{
                                height: { xs: 24, md: 28 },
                                mr: 1,
                            }}
                        />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    fontSize: '1.125rem',
                                    letterSpacing: '-0.01em'
                                }}
                            >
                                aidex.co
                            </Typography>
                    </Box>

                    {/* Desktop nav or mobile hamburger */}
                    {isMobile ? (
                        <IconButton
                            aria-label="open drawer"
                            edge="end"
                            onClick={handleDrawerToggle}
                            sx={{
                                color: '#1D1D1F',
                                p: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 122, 255, 0.08)'
                                }
                            }}
                        >
                            <MenuIcon fontSize="medium" />
                        </IconButton>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {navItems.map(item => (
                                        <Button
                                            key={item}
                                            color="inherit"
                                            onMouseEnter={() => handleMouseEnter(item)}
                                            onMouseLeave={handleMouseLeave}
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                fontWeight: 400,
                                                fontSize: '1rem',
                                                color: activeItem === item ? theme.palette.primary.main : theme.palette.text.primary,
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: `${theme.palette.primary.main}14`,
                                                    color: theme.palette.primary.main
                                                }
                                            }}
                                            onClick={() => {
                                                const sectionId =
                                                    item === 'header.about'
                                                        ? 'structure'
                                                        : item === 'header.services'
                                                            ? 'capabilities'
                                                            : item === 'header.contact'
                                                                ? 'contact_us'
                                                                : '';
                                                const section = document.getElementById(sectionId);
                                                if (section) {
                                                    section.scrollIntoView({ behavior: 'smooth' });
                                                }
                                            }}
                                        >
                                            {t(item)}
                                        </Button>
                                    ))}
                                </Box>
                                
                                {/* Dark Mode Toggle */}
                                <IconButton
                                    onClick={toggleDarkMode}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        p: 1,
                                        '&:hover': {
                                            backgroundColor: `${theme.palette.primary.main}14`
                                        }
                                    }}
                                >
                                    {darkMode ? <LightModeIcon fontSize="medium" /> : <DarkModeIcon fontSize="medium" />}
                                </IconButton>
                                
                                <LanguageSwitcher />
                            </Box>
                        )}
                </Toolbar>
            </Container>

                <Drawer
                    anchor="right"
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true
                    }}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                    PaperProps={{
                        sx: {
                            width: '100%',
                            bgcolor: theme.palette.background.paper,
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            color: theme.palette.text.primary,
                            px: 3,
                            pt: 3
                        }
                    }}
                >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                            component="img"
                            src={Logo}
                            alt="aidex.co logo"
                            sx={{ height: 28, mr: 1 }}
                        />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    fontSize: '1.125rem'
                                }}
                            >
                                aidex.co
                            </Typography>
                        </Box>
                        <IconButton onClick={handleDrawerToggle} sx={{ color: theme.palette.text.primary }}>
                            <CloseIcon />
                        </IconButton>
                </Box>

                <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {navItems.map(item => (
                        <ListItem key={item} disablePadding>
                            <ListItemButton
                                onClick={() => {
                                    const sectionId =
                                        item === 'header.about'
                                            ? 'structure'
                                            : item === 'header.services'
                                                ? 'capabilities'
                                                : item === 'header.contact'
                                                    ? 'contact_us'
                                                    : '';
                                    const section = document.getElementById(sectionId);
                                    if (section) {
                                        section.scrollIntoView({ behavior: 'smooth' });
                                        setMobileOpen(false);
                                    }
                                }}
                                    sx={{
                                        width: '100%',
                                        py: 2,
                                        borderRadius: 2,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: `${theme.palette.primary.main}14`
                                        }
                                    }}
                            >
                                <ListItemText
                                    primary={t(item)}
                                    primaryTypographyProps={{
                                        textAlign: 'center',
                                        fontWeight: 400,
                                        color: theme.palette.text.primary,
                                        fontSize: '1.125rem'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
                        {/* Dark Mode Toggle for Mobile */}
                        <IconButton
                            onClick={toggleDarkMode}
                            sx={{
                                color: theme.palette.text.primary,
                                p: 1,
                                '&:hover': {
                                    backgroundColor: `${theme.palette.primary.main}14`
                                }
                            }}
                        >
                            {darkMode ? <LightModeIcon fontSize="medium" /> : <DarkModeIcon fontSize="medium" />}
                        </IconButton>
                        <LanguageSwitcher />
                    </Box>
                </List>
            </Drawer>
        </AppBar>
    );
}